// frontend/src/components/FavoritesView.js
import React, { useMemo, useState } from "react";
import { BACKEND_URL } from "../config";

function sumMealItems(items = []) {
  const rows = Array.isArray(items) ? items : [];
  return rows.reduce(
    (acc, it) => {
      acc.calories += Number(it?.calories) || 0;
      acc.protein += Number(it?.protein) || 0;
      acc.carbs += Number(it?.carbs) || 0;
      acc.fats += Number(it?.fats) || 0;
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fats: 0 }
  );
}

/**
 * FavoritesView
 * Supports:
 *  - Food favorites (meal context)
 *  - Exercise favorites (workouts context)
 *
 * Props:
 *  - favorites: array
 *  - userId: string
 *  - token: string (required for exercise endpoints)
 *  - kind: "food" | "exercise" (optional; inferred if omitted)
 *  - onLogged: callback
 *  - onDeleted: callback
 */
function FavoritesView({ favorites = [], userId, token, kind, onLogged, onDeleted, showToast }) {
  const [mealTypeById, setMealTypeById] = useState({});
  const [durationById, setDurationById] = useState({});
  const [busyId, setBusyId] = useState(null);

  const inferredKind = useMemo(() => {
    if (kind === "food" || kind === "exercise") return kind;
    const hasExercise = (favorites || []).some((f) => f && (f.exercise_name || f.calories_per_minute != null));
    return hasExercise ? "exercise" : "food";
  }, [kind, favorites]);

  const safeFavorites = useMemo(() => {
    const rows = Array.isArray(favorites) ? favorites : [];
    return rows
      .filter(Boolean)
      .filter((f) => {
        // type filtering (supports older docs without favorite_type)
        if (inferredKind === "food") {
          const ft = f.favorite_type;
          if (ft && ft !== "food" && ft !== "meal") return false;
          return Boolean(f.favorite_id || f.food_name || f.meal_name || f.items);
        }
        // exercise
        const ft = f.favorite_type;
        if (ft && ft !== "exercise") return false;
        return Boolean(f.favorite_id || f.exercise_name);
      });
  }, [favorites, inferredKind]);

  // ---------- FOOD ----------
  const handleLogFoodFavorite = async (fav) => {
    if (!userId) return;

    const key = fav.favorite_id || fav.food_name;
    setBusyId(key);

    const meal_type = mealTypeById[key] || fav.meal_type || "lunch";

    const payload = {
      user_id: userId,
      food_name: fav.food_name,
      calories: fav.calories,
      protein: fav.protein || 0,
      carbs: fav.carbs || 0,
      fats: fav.fats || 0,
      quantity: fav.quantity || "1 serving",
      meal_type,
      notes: "",
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/food/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to log Nutrition");
      }

      showToast?.("Logged food from favorites!");
      onLogged?.();
    } catch (e) {
      showToast?.("Error logging favorite: " + e.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteFoodFavorite = async (fav) => {
    const favId = fav.favorite_id;
    if (!favId) {
      showToast?.("This favorite is missing favorite_id. Update backend to delete by user_id + food_name.", "error");
      return;
    }

    if (!window.confirm(`Remove "${fav.food_name}" from favorites?`)) return;

    const key = fav.favorite_id || fav.food_name;
    setBusyId(key);

    try {
      const res = await fetch(`${BACKEND_URL}/api/food/favorites/${favId}`, { method: "DELETE" });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to delete favorite");
      }

      onDeleted?.();
    } catch (e) {
      showToast?.("Error deleting favorite: " + e.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  
  const handleLogMealFavorite = async (fav) => {
    if (!userId) return;
    if (!token) {
      showToast?.("Missing token. Please log in again.", "error");
      return;
    }

    const key = fav.favorite_id || fav.meal_name || "meal";
    setBusyId(key);

    const meal_type = mealTypeById[key] || fav.meal_type || "lunch";
    const items = Array.isArray(fav.items) ? fav.items : [];

    if (!items.length) {
      showToast?.("This meal favorite has no items.", "error");
      setBusyId(null);
      return;
    }

    try {
      // log each item as a food log entry
      for (const it of items) {
        const payload = {
          user_id: userId,
          food_name: it.food_name,
          calories: it.calories,
          protein: it.protein || 0,
          carbs: it.carbs || 0,
          fats: it.fats || 0,
          quantity: it.quantity || "1 serving",
          meal_type,
          notes: "",
        };

        const res = await fetch(`${BACKEND_URL}/api/food/log`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Failed to log meal favorite");
        }
      }

      showToast?.("Logged meal from favorites!");
      onLogged?.();
    } catch (e) {
      showToast?.("Error logging meal favorite: " + e.message, "error");
    } finally {
      setBusyId(null);
    }
  };

// ---------- EXERCISE ----------
  const handleLogExerciseFavorite = async (fav) => {
    if (!userId) return;
    if (!token) {
      showToast?.("Missing auth token. Please log in again.", "error");
      return;
    }

    const key = fav.favorite_id || fav.exercise_name;
    setBusyId(key);

    const dur = Number(durationById[key] ?? fav.default_duration_minutes ?? 30) || 30;
    const rate = Number(fav.calories_per_minute);
    const fallbackCalories = Number(fav.calories_burned) || 0;
    const calories_burned = Number.isFinite(rate) && rate > 0 ? Math.round(rate * dur) : fallbackCalories;

    const payload = {
      user_id: userId,
      exercise_name: fav.exercise_name,
      duration_minutes: dur,
      calories_burned,
      exercise_type: "favorite",
      notes: "",
    };

    try {
      const res = await fetch(`${BACKEND_URL}/api/exercise/log`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to log exercise");
      }

      showToast?.("Logged exercise from favorites!");
      onLogged?.();
    } catch (e) {
      showToast?.("Error logging favorite: " + e.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteExerciseFavorite = async (fav) => {
    const favId = fav.favorite_id;
    if (!favId) {
      showToast?.("This favorite is missing favorite_id.", "error");
      return;
    }
    if (!token) {
      showToast?.("Missing auth token. Please log in again.", "error");
      return;
    }

    if (!window.confirm(`Remove "${fav.exercise_name}" from favorites?`)) return;

    const key = fav.favorite_id || fav.exercise_name;
    setBusyId(key);

    try {
      const res = await fetch(`${BACKEND_URL}/api/exercise/favorites/${favId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to delete favorite");
      }

      onDeleted?.();
    } catch (e) {
      showToast?.("Error deleting favorite: " + e.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="favorites-view">
      <h1 className="heading-2">Favorites</h1>

      {safeFavorites.length === 0 ? (
        <p className="body-small" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
          No favorites saved yet
        </p>
      ) : (
        <div className="favorites-list" style={{ display: "grid", gap: 12 }}>
          {safeFavorites.map((fav) => {
            const key = fav.favorite_id || fav.food_name || fav.exercise_name;

            if (inferredKind === "exercise") {
              const dur = durationById[key] ?? fav.default_duration_minutes ?? 30;
              const rate = fav.calories_per_minute;

              return (
                <div
                  key={key}
                  style={{
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border-light)",
                    borderRadius: 12,
                    padding: 14,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{fav.exercise_name}</div>
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>
                        {rate != null ? `${rate} cal/min` : "—"}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 220 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                          Duration (min)
                        </label>
                        <input
                          className="form-input"
                          type="number"
                          min="1"
                          value={dur}
                          onChange={(e) => setDurationById((prev) => ({ ...prev, [key]: e.target.value }))}
                        />
                      </div>

                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="btn-primary"
                          onClick={() => handleLogExerciseFavorite(fav)}
                          disabled={busyId === key}
                          style={{ flex: 1 }}
                        >
                          {busyId === key ? "..." : "Log"}
                        </button>

                        <button
                          className="btn-secondary"
                          onClick={() => handleDeleteExerciseFavorite(fav)}
                          disabled={busyId === key}
                          style={{ flex: 1, borderColor: "#dc2626", color: "#dc2626" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }


            // Meal favorite (named collection of items)
            if (fav.favorite_type === "meal" || Array.isArray(fav.items)) {
              const mealKey = key;
              const mealTypeValue = mealTypeById[mealKey] || fav.meal_type || "lunch";
              const totals = sumMealItems(fav.items || []);
              const count = Array.isArray(fav.items) ? fav.items.length : 0;

              return (
                <div
                  key={mealKey}
                  style={{
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border-light)",
                    borderRadius: 12,
                    padding: 14,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 800 }}>🍽️ {fav.meal_name || "Meal Favorite"}</div>
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>
                        {count} item{count === 1 ? "" : "s"} • {Math.round(totals.calories)} cal •
                        P {Math.round(totals.protein)}g • C {Math.round(totals.carbs)}g • F {Math.round(totals.fats)}g
                      </div>
                      <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                        {Array.isArray(fav.items) ? fav.items.slice(0, 4).map((it) => it.food_name).filter(Boolean).join(", ") : ""}
                        {Array.isArray(fav.items) && fav.items.length > 4 ? "…" : ""}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 190 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                          Meal
                        </label>
                        <select
                          className="form-input"
                          value={mealTypeValue}
                          onChange={(e) => setMealTypeById((prev) => ({ ...prev, [mealKey]: e.target.value }))}
                        >
                          <option value="breakfast">Breakfast</option>
                          <option value="lunch">Lunch</option>
                          <option value="dinner">Dinner</option>
                          <option value="snack">Snack</option>
                          <option value="beverage">Beverage</option>
                        </select>
                      </div>

                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="btn-primary"
                          onClick={() => handleLogMealFavorite(fav)}
                          disabled={busyId === mealKey}
                          style={{ flex: 1 }}
                        >
                          {busyId === mealKey ? "..." : "Log"}
                        </button>

                        <button
                          className="btn-secondary"
                          onClick={() => handleDeleteFoodFavorite(fav)}
                          disabled={busyId === mealKey}
                          style={{ flex: 1, borderColor: "#dc2626", color: "#dc2626" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            // food
            const mealTypeValue = mealTypeById[key] || fav.meal_type || "lunch";

            return (
              <div
                key={key}
                style={{
                  background: "var(--bg-secondary)",
                  border: "1px solid var(--border-light)",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{fav.food_name}</div>
                    <div className="body-small" style={{ color: "var(--text-muted)" }}>
                      {fav.calories} cal • P {fav.protein || 0}g • C {fav.carbs || 0}g • F {fav.fats || 0}g
                    </div>
                    {fav.quantity && (
                      <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
                        Qty: {fav.quantity}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 190 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="body-small" style={{ color: "var(--text-secondary)" }}>
                        Meal
                      </label>
                      <select
                        className="form-input"
                        value={mealTypeValue}
                        onChange={(e) => setMealTypeById((prev) => ({ ...prev, [key]: e.target.value }))}
                      >
                        <option value="breakfast">Breakfast</option>
                        <option value="lunch">Lunch</option>
                        <option value="dinner">Dinner</option>
                        <option value="snack">Snack</option>
                        <option value="beverage">Beverage</option>
                      </select>
                    </div>

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="btn-primary"
                        onClick={() => handleLogFoodFavorite(fav)}
                        disabled={busyId === key}
                        style={{ flex: 1 }}
                      >
                        {busyId === key ? "..." : "Log"}
                      </button>

                      <button
                        className="btn-secondary"
                        onClick={() => handleDeleteFoodFavorite(fav)}
                        disabled={busyId === key}
                        style={{ flex: 1, borderColor: "#dc2626", color: "#dc2626" }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FavoritesView;
